import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import api from '../../../api';
import {
  SectionErrorKind,
  WARMING_MAX_POLLS,
  errorKind,
  isWarming,
} from '../browserLadder/useLadderSection';
import type {
  WebWorkMixResponse,
  WebWorkMixWarmingResponse,
  WebWorkSourceFilter,
} from './types';

/**
 * Loads GET /platform-stats/web-work-mix?weeks=N&source=… with the same
 * contract as the Browser Ladder sections: a cold cache answers
 * `202 { status: 'warming', retryAfterMs }` and the hook polls until the
 * payload is ready; a response for an older (weeks, source) pair is dropped
 * once a newer request has been issued.
 */

export interface WebWorkMixState {
  data: WebWorkMixResponse | null;
  loading: boolean;
  /** True while the server has said "warming" and we are polling for it. */
  warming: boolean;
  error: SectionErrorKind | null;
  /** Re-request, bypassing the server cache. */
  retry: () => void;
}

const DEFAULT_RETRY_MS = 3_000;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

class WarmingTimeoutError extends Error {}

export function webWorkMixPath(
  weeks: number,
  source: WebWorkSourceFilter,
  bypassCache: boolean,
): string {
  return `/platform-stats/web-work-mix?weeks=${weeks}&source=${source}${
    bypassCache ? '&refresh=1' : ''
  }`;
}

export function useWebWorkMix(
  weeks: number,
  source: WebWorkSourceFilter,
): WebWorkMixState {
  const { getAccessTokenSilently } = useAuth0();
  const [data, setData] = useState<WebWorkMixResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [warming, setWarming] = useState(false);
  const [error, setError] = useState<SectionErrorKind | null>(null);
  const [attempt, setAttempt] = useState(0);
  const requestSeq = useRef(0);
  const forceRefresh = useRef(false);

  useEffect(() => {
    const seq = ++requestSeq.current;
    const refresh = forceRefresh.current;
    forceRefresh.current = false;
    const current = () => seq === requestSeq.current;
    const fetchOnce = async (bypassCache: boolean) => {
      const token = await getAccessTokenSilently();
      return api.get<WebWorkMixResponse | WebWorkMixWarmingResponse>(
        webWorkMixPath(weeks, source, bypassCache),
        { headers: { Authorization: `Bearer ${token}` } },
      );
    };
    const load = async () => {
      setLoading(true);
      setWarming(false);
      setError(null);
      try {
        let response = await fetchOnce(refresh);
        let polls = 0;
        while (current() && isWarming(response.data)) {
          if (++polls > WARMING_MAX_POLLS) throw new WarmingTimeoutError();
          setWarming(true);
          await sleep(response.data.retryAfterMs ?? DEFAULT_RETRY_MS);
          if (!current()) return;
          response = await fetchOnce(false);
        }
        if (!current() || isWarming(response.data)) return;
        setData(response.data);
      } catch (err) {
        if (!current()) return;
        setData(null);
        setError(
          err instanceof WarmingTimeoutError ? 'timeout' : errorKind(err),
        );
      } finally {
        if (current()) {
          setLoading(false);
          setWarming(false);
        }
      }
    };
    load();
    return () => {
      requestSeq.current++;
    };
  }, [weeks, source, attempt, getAccessTokenSilently]);

  const retry = useCallback(() => {
    forceRefresh.current = true;
    setAttempt((n) => n + 1);
  }, []);

  return { data, loading, warming, error, retry };
}
